import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ActionButton, Buttons, CancelButton, Drawer, Table } from '~/components';
import { useApi, useToast, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm, useIsMounted } from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import OptionSelect from './OptionSelect';

export default function ExpenseCategoryMappings({ onClose }) {
  const { workspace } = useWorkspace();
  const api = useApi();
  const toast = useToast();
  const [{ isSubmitting, saved }, form] = useForm();
  const isMounted = useIsMounted();
  const [dirty, setDirty] = useState(false);
  const dirtyCheck = useDirtyCheck(() => dirty);
  const { sageConnectionId } = useParams();

  const [{ categories, accounts, purchasingItems, departments, isDocuments, isReady }, setState] = useState({
    categories: null,
    isReady: false,
  });

  useEffect(() => {
    (async () => {
      const sage = api.www.workspaces(workspace.id).sage(sageConnectionId);
      const { data: categories } = await sage.getExpenseCategoryMappings();
      const { data: accounts } = await sage.getExpenseAccounts();
      const { data: departments } = await sage.getDepartments();
      const { data: configuration } = await sage.getConfiguration();
      const isDocuments = configuration?.syncMethod === 'documents';
      const { data: purchasingItems } = isDocuments ? await sage.getPurchasingItems() : { data: [] };
      if (!isMounted.current) return;
      setState({ categories, accounts, purchasingItems, departments, isDocuments, isReady: true });
    })();
  }, [api, workspace.id, isMounted, sageConnectionId]);

  const updateMapping = ({ expenseCategoryId, ...patch }) => {
    setDirty(true);
    setState((state) => ({
      ...state,
      categories: state.categories.map((c) => (c.id === expenseCategoryId ? { ...c, ...patch } : c)),
    }));
  };

  return (
    <Drawer
      isOpen
      title="Expense Category Mappings"
      width="70rem"
      onBeforeClose={({ setIsOpen }) => dirtyCheck(() => setIsOpen(false))}
      onClose={onClose}>
      {(closeDrawer) => {
        const handleCloseClick = () => dirtyCheck(() => closeDrawer());

        const handleSave = async () => {
          form.submit();
          try {
            const mappings = categories.map((c) => ({
              expenseCategoryId: c.id,
              sageAccountId: c.sageAccountId,
              sageDepartmentId: c.sageDepartmentId,
              sageItemKey: c.sageItemKey,
            }));
            await api.www.workspaces(workspace.id).sage(sageConnectionId).updateExpenseCategoryMappings(mappings);
            form.save();
            closeDrawer();
          } catch (error) {
            toast.error(error.message);
            form.done();
          }
        };

        if (!isReady) return <PageLoader />;

        return (
          <>
            <Table style={{ marginBottom: '2rem' }}>
              <Table.BoxHeader>
                <Table.Column width="40%">Expense Category</Table.Column>
                <Table.Column>{isDocuments ? 'Sage Item' : 'Sage GL Account'}</Table.Column>
                <Table.Column>Sage Department</Table.Column>
              </Table.BoxHeader>
              <Table.Body>
                {categories.map((category) => (
                  <Table.BoxRow key={category.id}>
                    <Table.Cell>{category.name}</Table.Cell>
                    <Table.Cell>
                      <OptionSelect
                        defaultOptions={isDocuments ? purchasingItems : accounts}
                        typeLabel={isDocuments ? 'Sage Item' : 'Sage GL Account'}
                        value={isDocuments ? category.sageItemKey : category.sageAccountId}
                        onChange={(event) =>
                          updateMapping({
                            expenseCategoryId: category.id,
                            [isDocuments ? 'sageItemKey' : 'sageAccountId']: event.target.value || null,
                            ...(event.target.value ? {} : { sageDepartmentId: null }),
                          })
                        }
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <OptionSelect
                        defaultOptions={departments}
                        typeLabel="Sage Department"
                        disabled={isDocuments ? !category.sageItemKey : !category.sageAccountId}
                        value={category.sageDepartmentId}
                        onChange={(event) =>
                          updateMapping({
                            expenseCategoryId: category.id,
                            sageDepartmentId: event.target.value || null,
                          })
                        }
                      />
                    </Table.Cell>
                  </Table.BoxRow>
                ))}
              </Table.Body>
            </Table>
            <Drawer.Actions>
              <Buttons align="right">
                <CancelButton onClick={handleCloseClick}>Close</CancelButton>
                <ActionButton isLoading={isSubmitting} ok={saved} onClick={handleSave}>
                  Save &amp; Close
                </ActionButton>
              </Buttons>
            </Drawer.Actions>
          </>
        );
      }}
    </Drawer>
  );
}
